import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Divider } from './common/Catalyst/divider';
import { Button } from './common/Catalyst/button';
import { Dialog, DialogBody, DialogTitle } from './common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  ErrorMessage,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { hoaService, homeService, violationService } from './clients/ApiClients';
import {
  HoaReturn,
  HomeReturn,
  ViolationReturn,
  ViolationUpdate,
} from './clients/rust-api-client';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Textarea } from './common/Catalyst/textarea';
import ShowAlert from './ShowAlert';
import { ErrorPage } from './common/Custom/ErrorPage';
import { fineIsOutstanding, formatMoney } from './utils/billingHelpers';
import LoadingSpinner from './LoadingSpinner';
import { getUserIdFromToken, getUserTypeFromToken } from './utils/TokenUtils';
import {
  canManageViolationAttachments,
  canEditViolation,
  canModerateViolationComments,
  canPostInternalComment,
  canPostViolationComment,
  canViewViolationComments,
} from './constants/UserTypes';
import ViolationCommentThread from './violation/comments/ViolationCommentThread';
import { useLinkedCommentId } from './violation/comments/useLinkedCommentId';
import { violationEvents } from './violation/comments/activityEvents';
import ViolationAttachments from './violation/comments/ViolationAttachments';
import { ViolationPropertiesPanel } from './violation/linear/ViolationPropertiesPanel';
import { StatusIcon } from './violation/linear/StatusIcon';
import { positionIn, readListContext } from './violation/linear/listContext';
import { changedDayMillis, shortRef } from './violation/linear/violationMeta';
import { apiErrorMessage } from './utils/apiErrors';
import {
  ChevronDownIcon as ChevronDownMini,
  ChevronUpIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/16/solid';
import {
  fresher,
  useSyncedHoa,
  useSyncedHome,
  useSyncedViolation,
} from './sync/hooks';
import { recordFines } from './sync/fines';
import { updateViolation } from './sync/writes';
import { CreateActions } from './shell/CreateActions';

interface TouchedFields {
  [key: string]: boolean;
}

interface FormErrors {
  [key: string]: string;
}

/**
 * What the edit dialog holds while it is open.
 *
 * Deliberately **not** `ViolationUpdate`. The update payload wants epoch
 * milliseconds for the dates and a decimal string for `fine_amount`; the
 * dialog's inputs hold `YYYY-MM-DD` strings and the amount as typed.
 * `handleSave` is what converts between the two.
 *
 * This state used to be typed `ViolationUpdate` and seeded by spreading a
 * `ViolationReturn` behind a cast. The cast hid two disagreements: the dates
 * are strings on the way in and numbers on the way out, and `ViolationReturn`
 * has no `fine_amount` at all — it carries a `fine` object. So `fine_amount`
 * was always `undefined`, the input opened blank on a fined violation, and
 * `handleSave` sent the literal string `"undefined"` on every save.
 */
interface ViolationEditForm {
  violation_type?: string | null;
  severity?: string | null;
  status?: string | null;
  description?: string | null;
  message?: string | null;
  fine_amount?: string | null;
  due_date?: string | null;
  follow_up_date?: string | null;
  resolution_date?: string | null;
  image_url?: string | null;
  document_url?: string | null;
}

// Helper to convert Unix timestamp or date string to YYYY-MM-DD for date inputs
const timestampToDateInput = (value: number | string | null | undefined): string => {
  if (!value) return '';

  // If it's already a date string (YYYY-MM-DD), return it as-is
  if (typeof value === 'string') {
    // Validate it's a proper date string
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    // Try to parse other string formats
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    return '';
  }

  // It's a number (Unix timestamp in seconds)
  const date = new Date(value * 1000);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const ViolationDetailPage: React.FC = () => {
  const { violationId } = useParams<{
    violationId: string;
  }>();
  // The reply a notification pointed at, if one did.
  const linkedCommentId = useLinkedCommentId();
  const [loading, setLoading] = useState(true);
  const [ownViolation, setViolation] = useState<ViolationReturn | null>(null);
  // The sync engine's local copy when it has this violation (#561), or
  // what this page last got back from the server if that is newer.
  const syncedViolation = useSyncedViolation(violationId);
  const violation = fresher(syncedViolation, ownViolation);
  const haveSynced = !!syncedViolation;
  // The violation's home, for the panel's link to it (#617). A violation
  // carries only home_id; the home carries the HOA its page lives under.
  const [home, setHome] = useState<HomeReturn | null>(null);
  const [hoa, setHoa] = useState<HoaReturn | null>(null);
  const [showSaveError, setShowSaveError] = useState(false);
  const [saveErrorTitle, setSaveErrorTitle] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');

  const [showFetchError, setShowFetchError] = useState(false);
  const [fetchErrorTitle, setFetchErrorTitle] = useState<string>('');
  const [fetchError, setFetchError] = useState<string>('');
  // Bumped by the error page's "Try again" to run the load again.
  const [reloadKey, setReloadKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isRecordingPayment, setIsRecordingPayment] =
    useState<boolean>(false);
  const [editedViolation, setEditedViolation] =
    useState<ViolationEditForm | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // The page reads the role and hands children booleans; children never
  // see a UserType. Identity is separate from permission, so the user's
  // own id travels alongside rather than inside those gates.
  const userType = getUserTypeFromToken();
  const currentUserId = getUserIdFromToken();
  const canViewComments = canViewViolationComments(userType);
  const activityEvents = useMemo(
    () => (violation ? violationEvents(violation) : []),
    [violation]
  );
  const canComment = canPostViolationComment(userType);
  const canPostInternal = canPostInternalComment(userType);
  const canModerateComments = canModerateViolationComments(userType);
  const canAttach = canManageViolationAttachments(userType);
  const canEdit = canEditViolation(userType);

  // In-place property edits (#603): one field at a time, saved on choose,
  // through the write queue (#561) — shown at once, undone if refused.
  const [savingField, setSavingField] = useState<string | null>(null);
  const [propertyError, setPropertyError] = useState<string | null>(null);

  // Previous / next within the list this violation was opened from.
  const navigate = useNavigate();
  const listContext = readListContext();
  const position = violationId ? positionIn(listContext, violationId) : null;
  const backTo = listContext?.from ?? '/violations';

  const severityOptions = ['Minor', 'Moderate', 'Major', 'Critical'];
  // Status values must match API format (PascalCase), labels are user-friendly
  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'InProgress', label: 'In Progress' },
    { value: 'Resolved', label: 'Resolved' },
    { value: 'Escalated', label: 'Escalated' },
    { value: 'Appealed', label: 'Appealed' },
  ];
  const typeOptions = [
    'Maintenance',
    'Noise',
    'Architectural',
    'Parking',
    'Landscaping',
    'Pets',
    'Trash',
    'Other',
  ];

  useEffect(() => {
    const fetchViolation = async () => {
      if (haveSynced) {
        setLoading(false);
        // The copy has everything but the fine, which is money and so is
        // read from REST (`sync/fines.ts`). In the background: the page is
        // already showing.
        violationService
          .getViolationById({ violationId: violationId! })
          .then((r) => recordFines([r.data]))
          // The fine row stays as it was: no alert for a background read.
          .catch((err) => console.error('Error refreshing the fine:', err));
        return;
      }
      try {
        setLoading(true);
        const response = await violationService.getViolationById({
          violationId: violationId!,
        });
        setViolation(response.data);
        recordFines([response.data]);
        setShowFetchError(false);
      } catch (err) {
        console.error('Error fetching violation:', err);
        const errorMessage = apiErrorMessage(
          err,
          "Couldn't load the violation."
        );

        setFetchErrorTitle("Couldn't load violation details");
        setFetchError(errorMessage);
        setShowFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchViolation();
  }, [violationId, haveSynced, reloadKey]);

  const homeId = violation?.home_id;
  // From the sync engine's copy when it has them (#561); fetched otherwise.
  const syncedHome = useSyncedHome(homeId);
  const haveSyncedHome = !!syncedHome;
  useEffect(() => {
    if (!homeId || haveSyncedHome) return;
    let live = true;
    homeService
      .getHome({ id: homeId })
      .then((r) => live && setHome(r.data))
      // Not worth an alert: the panel keeps showing the id.
      .catch((err) => console.error('Error fetching the violation\'s home:', err));
    return () => {
      live = false;
    };
  }, [homeId, haveSyncedHome]);
  const violationHome = syncedHome ?? (home?.id === homeId ? home : null);

  // Its HOA's name, for the link beside the home's.
  const hoaId = violationHome?.hoa_id;
  const syncedHoa = useSyncedHoa(hoaId);
  const haveSyncedHoa = !!syncedHoa;
  useEffect(() => {
    if (!hoaId || haveSyncedHoa) return;
    let live = true;
    hoaService
      .getHoa({ id: hoaId })
      .then((r) => live && setHoa(r.data))
      // The row keeps linking by id.
      .catch((err) => console.error('Error fetching the violation\'s HOA:', err));
    return () => {
      live = false;
    };
  }, [hoaId, haveSyncedHoa]);
  const violationHoa = syncedHoa ?? (hoa?.id === hoaId ? hoa : null);

  const reloadViolation = async () => {
    try {
      const response = await violationService.getViolationById({
        violationId: violationId!,
      });
      setViolation(response.data);
      recordFines([response.data]);
    } catch (err) {
      console.error('Error reloading violation:', err);
    }
  };

  const handlePropertyUpdate = async (patch: ViolationUpdate, field: string) => {
    if (!violationId) return;
    setSavingField(field);
    setPropertyError(null);
    try {
      // Status, severity and dates go through the write queue: shown at
      // once, taken back if refused. Without a copy it is a plain request
      // and the page shows the answer.
      const saved = await updateViolation(violationId, patch);
      if (saved) setViolation(saved);
    } catch (err) {
      console.error(`Error updating violation ${field}:`, err);
      setPropertyError(
        apiErrorMessage(err, 'The change was not saved. Please try again.')
      );
    } finally {
      setSavingField(null);
    }
  };

  // j / k step through the list, as in Linear — but never while the user
  // is typing, and never with a dialog open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isEditing || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) ||
          t.closest('[role="dialog"], [role="listbox"]'))
      )
        return;
      if (e.key === 'k' && position?.prevId) navigate(`/violations/${position.prevId}`);
      if (e.key === 'j' && position?.nextId) navigate(`/violations/${position.nextId}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEditing, position?.prevId, position?.nextId, navigate]);

  const handleEdit = () => {
    if (violation) {
      // Built field by field rather than spread-and-cast, so that anything
      // the dialog can edit has to be named here. `fine_amount` comes off
      // the `fine` block — it is the one field with no counterpart on
      // `ViolationReturn`, and the one a spread silently dropped.
      setEditedViolation({
        violation_type: violation.violation_type,
        severity: violation.severity,
        status: violation.status,
        description: violation.description,
        message: violation.message,
        fine_amount: violation.fine?.amount ?? null,
        due_date: violation.due_date ?? null,
        follow_up_date: violation.follow_up_date ?? null,
        resolution_date: violation.resolution_date ?? null,
        image_url: violation.image_url ?? null,
        document_url: violation.document_url ?? null,
      });
      setIsEditing(true);
      setTouchedFields({});
      setFormErrors({});
    }
  };

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSave = async () => {
    if (!editedViolation || !violation) return;

    setIsSaving(true);
    setShowSaveError(false);
    setShowSuccessAlert(false);

    // Create a clean update payload
    const updatePayload: ViolationUpdate = {};

    // Only include fields that have changed
    if (editedViolation.violation_type !== violation.violation_type) {
      updatePayload.violation_type =
        editedViolation.violation_type?.toLowerCase();
    }

    if (editedViolation.severity !== violation.severity) {
      updatePayload.severity = editedViolation.severity?.toLowerCase();
    }

    if (editedViolation.description !== violation.description) {
      updatePayload.description = editedViolation.description;
    }

    if (editedViolation.message !== violation.message) {
      updatePayload.message = editedViolation.message;
    }

    if (editedViolation.status !== violation.status) {
      updatePayload.status = editedViolation.status
        ?.toLowerCase()
        .replace(' ', '_');
    }

    // Dates go as epoch milliseconds, and only when the day moved (#606).
    const dueDate = changedDayMillis(editedViolation.due_date, violation.due_date);
    if (dueDate !== undefined) {
      updatePayload.due_date = dueDate;
    }

    const followUpDate = changedDayMillis(
      editedViolation.follow_up_date,
      violation.follow_up_date
    );
    if (followUpDate !== undefined) {
      updatePayload.follow_up_date = followUpDate;
    }

    // Handle URLs - only include if they've changed and are valid URLs
    if (
      editedViolation.image_url &&
      editedViolation.image_url !== violation.image_url
    ) {
      try {
        new URL(editedViolation.image_url); // validate URL
        updatePayload.image_url = editedViolation.image_url;
      } catch (e) {
        console.error('Invalid image URL');
      }
    }

    if (
      editedViolation.document_url &&
      editedViolation.document_url !== violation.document_url
    ) {
      try {
        new URL(editedViolation.document_url); // validate URL
        updatePayload.document_url = editedViolation.document_url;
      } catch (e) {
        console.error('Invalid document URL');
      }
    }

    // The fine is a ledger charge now, so the violation carries a
    // `fine` block rather than an amount column. Changing it reverses
    // the old charge and posts a new one on the backend.
    // A cleared field withdraws the fine, which `ViolationUpdate` spells
    // as `null`. Anything else goes as the decimal string the manager
    // typed — never `String()` of an unchecked value, which is how this
    // came to send "undefined" on every save of a fined violation.
    const currentFine = violation.fine?.amount ?? null;
    const editedFine = editedViolation.fine_amount?.trim()
      ? editedViolation.fine_amount.trim()
      : null;
    if (editedFine !== currentFine) {
      updatePayload.fine_amount = editedFine;
    }

    try {
      const response = await violationService.updateViolation({
        violationId: violationId!,
        violationUpdate: updatePayload,
      });

      // Update local state. The edit can change the fine, which is a
      // ledger write and so never goes through the write queue.
      setViolation(response.data);
      recordFines([response.data]);

      setIsEditing(false);
      setSuccessMessage('Violation updated successfully!');
      setShowSuccessAlert(true);
    } catch (err) {
      console.error('Error updating violation:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't save the violation.");

      setSaveErrorTitle("Couldn't update violation");
      setSaveError(errorMessage);
      setShowSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedViolation(null);
    setFormErrors({});
    setTouchedFields({});
  };

  /**
   * Record a payment against the fine.
   *
   * Writes a real payment applied to the fine's ledger entry — the
   * money reaches the home's balance and the payer file — rather than
   * stamping a date on the violation, which is what this screen used
   * to do. Sends no amount, which settles whatever is outstanding.
   */
  const handleRecordFinePayment = async () => {
    if (!violationId) return;
    setIsRecordingPayment(true);
    try {
      const response = await violationService.payFine({
        violationId,
        finePaymentRequest: {},
      });
      setViolation(response.data);
      recordFines([response.data]);
      setSuccessMessage(
        "Payment recorded. The homeowner's balance has been updated."
      );
      setShowSuccessAlert(true);
    } catch (err) {
      console.error('Error recording fine payment:', err);
      // A settled fine comes back 409 and an unparseable amount 400;
      // both carry a message worth showing rather than swallowing.
      const detail = apiErrorMessage(err, "Couldn't record the payment.");
      setSaveErrorTitle('Could Not Record the Payment');
      setSaveError(detail);
      setShowSaveError(true);
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string } }
  ) => {
    if (!editedViolation) return;
    const { name, value } = e.target;

    if (name === 'fine_amount') {
      // Remove any non-digit characters except decimal point
      const numericValue = value.replace(/[^\d.]/g, '');
      // Ensure only valid decimal numbers
      if (numericValue === '' || /^\d*\.?\d{0,2}$/.test(numericValue)) {
        setEditedViolation((prev) => ({
          ...prev,
          [name]: numericValue === '' ? null : numericValue,
        }));
      }
    } else {
      setEditedViolation((prev) => ({ ...prev, [name]: value }));
    }

    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof ViolationEditForm, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof ViolationEditForm, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const isFormValid = () => {
    // Check for any existing errors
    if (Object.keys(formErrors).length > 0) {
      return false;
    }

    // Check required fields
    const requiredFields = [
      'violation_type',
      'severity',
      'description',
      'message',
    ];

    return requiredFields.every((field) => {
      const value = editedViolation?.[field as keyof ViolationEditForm];
      // Check if the field has a value and it's not the default placeholder
      if (!value) return false;
      if (field === 'violation_type' && value === 'Select Type') return false;
      if (field === 'severity' && value === 'Select Severity') return false;
      return true;
    });
  };

  const isValidDate = (dateString: string | null | undefined): boolean => {
    if (!dateString) return true; // null/undefined is OK for optional dates
    // Check if it's a complete date string (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    // Check if it's a valid date (not like 2024-13-45)
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const validateField = (name: keyof ViolationEditForm, value: any): string => {
    switch (name) {
      case 'violation_type':
        return !typeOptions.includes(value)
          ? 'Please select a valid violation type'
          : '';
      case 'severity':
        return !severityOptions.includes(value)
          ? 'Please select a valid severity level'
          : '';
      case 'status':
        return !statusOptions.some((s) => s.value === value)
          ? 'Please select a valid status'
          : '';
      case 'description':
        return !value
          ? 'Description is required'
          : value.length > 300 // Changed from 500 to 300
            ? 'Description must be less than 300 characters'
            : '';
      case 'message':
        return !value ? 'Message is required' : '';
      case 'fine_amount':
        if (!value) return '';
        if (isNaN(value) || Number(value) < 0) {
          return 'Fine amount must be a positive number';
        }
        if (value > 10000) {
          return 'Fine amount cannot exceed $10,000.00';
        }
        return '';
      case 'due_date':
      case 'follow_up_date':
      case 'resolution_date':
        return !isValidDate(value) ? 'Please enter a valid date' : '';
      case 'image_url':
      case 'document_url':
        if (!value) return '';
        return !isValidUrl(value)
          ? 'Please enter a valid URL (e.g., https://example.com)'
          : '';
      default:
        return '';
    }
  };

  // With the row in the sync store there is nothing to wait for: render
  // it on the first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  // A failed load leaves nothing to show; say so rather than render an
  // empty page (#652).
  if (!violation) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage={'violations'} />}
        navbar={<Navbar />}
      >
        <ErrorPage
          title="Couldn't load this violation"
          message={
            fetchError ||
            'It may have been deleted, or you may not have access to it.'
          }
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={'violations'} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      {/* Header strip: where you came from, what this is, where you are
          in the list, and the overflow menu (#603 Part 2 §3). */}
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link
          to={backTo}
          className="text-fg-muted hover:text-fg"
        >
          Violations
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        {violation && (
          <>
            <StatusIcon status={violation.status} />
            <span className="hidden font-mono text-[12px] text-fg-muted sm:inline">
              {shortRef(violation.id)}
            </span>
            <span className="min-w-0 truncate font-medium text-fg">
              {violation.description}
            </span>
          </>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {/* Another violation, or a work order, at this violation's home. */}
          {hoaId && homeId && (
            <span className="mr-2 flex items-center gap-2">
              <CreateActions scope={{ hoaId, homeId }} violation workOrder />
            </span>
          )}
          {position && (
            <>
              <span
                className="mr-1 text-[12px] tabular-nums text-fg-muted"
                aria-label={`Violation ${position.index + 1} of ${position.total}`}
              >
                {position.index + 1} / {position.total}
              </span>
              <button
                type="button"
                onClick={() => position.prevId && navigate(`/violations/${position.prevId}`)}
                disabled={!position.prevId}
                title="Previous (k)"
                aria-label="Previous violation"
                className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronUpIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => position.nextId && navigate(`/violations/${position.nextId}`)}
                disabled={!position.nextId}
                title="Next (j)"
                aria-label="Next violation"
                className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronDownMini className="h-4 w-4" />
              </button>
            </>
          )}
          {canEdit && (
            <Dropdown>
              <DropdownButton plain aria-label="More actions">
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                <DropdownItem onClick={handleEdit}>Edit all fields…</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
      </div>

      <ShowAlert
        alertType="success"
        alertTitle="Success"
        alertMessage={successMessage}
        isVisible={showSuccessAlert}
        onClose={() => setShowSuccessAlert(false)}
        timeout={5000}
        isInline={true}
      />
      <ShowAlert
        alertType="error"
        alertTitle={fetchErrorTitle}
        alertMessage={fetchError}
        isVisible={showFetchError}
        onClose={() => setShowFetchError(false)}
        timeout={5000}
        isInline={true}
      />
      <ShowAlert
        alertType="error"
        alertTitle="Change not saved"
        alertMessage={propertyError ?? ''}
        isVisible={propertyError !== null}
        onClose={() => setPropertyError(null)}
        timeout={8000}
        isInline={true}
      />
      <ShowAlert
        alertType="error"
        alertTitle={saveErrorTitle}
        alertMessage={saveError}
        isVisible={showSaveError && !isEditing}
        onClose={() => setShowSaveError(false)}
        timeout={8000}
        isInline={true}
      />

      {violation && (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0 space-y-8">
            <div>
              <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">
                {violation.description}
              </h1>
              <p className="mt-3 whitespace-pre-line text-[15px]/6 text-fg-secondary">
                {violation.message}
              </p>
            </div>

            {/* Money keeps its explicit step. Recording a payment writes to
                the ledger, so it is a labelled button with the amount on it,
                never an in-place control or a keyboard shortcut. */}
            {violation.fine && fineIsOutstanding(violation.fine) && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line px-4 py-3">
                <p className="text-[13px] text-fg-secondary">
                  <span className="font-medium text-fg">
                    {formatMoney(violation.fine.amount_outstanding)}
                  </span>{' '}
                  outstanding on this fine. It is a charge on the home&apos;s
                  account and appears on the homeowner&apos;s balance.
                </p>
                {canEdit && (
                  <Button
                    color="sky"
                    onClick={handleRecordFinePayment}
                    disabled={isRecordingPayment}
                  >
                    {isRecordingPayment
                      ? 'Recording…'
                      : `Record payment of ${formatMoney(violation.fine.amount_outstanding)}`}
                  </Button>
                )}
              </div>
            )}

            <section aria-labelledby="attachments-heading">
              <h2
                id="attachments-heading"
                className="mb-2 text-[13px] font-medium text-fg-muted"
              >
                Photos &amp; documents
              </h2>
              <ViolationAttachments
                violationId={violationId!}
                attachments={violation.attachments ?? []}
                canManage={canAttach}
                onChanged={reloadViolation}
              />
              {/* Deprecated URL columns, still shown for rows written
                  before attachments existed. */}
              {(violation.image_url || violation.document_url) && (
                <div className="mt-3 space-y-1 text-[13px]">
                  {[violation.image_url, violation.document_url]
                    .filter(Boolean)
                    .map((url) => (
                      <a
                        key={url!}
                        href={url!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block break-all text-accent-fg hover:underline"
                      >
                        {url} <span className="text-zinc-500">(legacy link)</span>
                      </a>
                    ))}
                </div>
              )}
            </section>

            {canViewComments && (
              <section aria-label="Activity">
                <ViolationCommentThread
                  violationId={violationId!}
                  events={activityEvents}
                  currentUserId={currentUserId}
                  canComment={canComment}
                  canPostInternal={canPostInternal}
                  canModerate={canModerateComments}
                  canAttach={canAttach}
                  highlightCommentId={linkedCommentId}
                />
              </section>
            )}
          </div>

          {/* On narrow screens the panel drops below the main column. */}
          <aside
            aria-label="Violation properties"
            className="lg:sticky lg:top-6 lg:self-start"
          >
            <ViolationPropertiesPanel
              violation={violation}
              canEdit={canEdit}
              saving={savingField}
              onUpdate={handlePropertyUpdate}
              home={violationHome}
              hoa={violationHoa}
            />
          </aside>
        </div>
      )}

      <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        className="w-full max-w-2xl mx-auto"
      >
        <div className="relative flex flex-col h-full">
          <div className="p-4 md:p-6">
            <DialogTitle className="text-xl font-semibold">
              Edit Violation
            </DialogTitle>
            <button
              onClick={() => setIsEditing(false)}
              className="absolute right-4 top-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full transition-colors duration-150"
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-5 w-5 text-zinc-600 dark:text-zinc-100" />
            </button>

            <Divider className="my-4" />

            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle={saveErrorTitle}
                alertMessage={saveError}
                isVisible={showSaveError}
                onClose={() => setShowSaveError(false)}
                timeout={5000}
                isInline={true}
              />
            </div>

            <DialogBody>
              <Fieldset>
                <FieldGroup>
                  {/* Type, Severity, and Status - Stack on mobile */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field>
                      <Label>Violation Type*</Label>
                      <FieldPicker
                        label="Violation Type"
                        options={typeOptions.map((type) => ({ value: type, label: type }))}
                        value={editedViolation?.violation_type}
                        onChange={(value) =>
                          handleInputChange({
                            target: {
                              name: 'violation_type',
                              value,
                              type: 'change',
                              checked: false,
                              nodeName: 'INPUT',
                            } as unknown as EventTarget & HTMLInputElement,
                          } as React.ChangeEvent<HTMLInputElement>)
                        }
                        placeholder="Select Type"
                        invalid={!!(touchedFields.violation_type && formErrors.violation_type)}
                      />
                      {touchedFields.violation_type &&
                        formErrors.violation_type && (
                          <ErrorMessage>
                            {formErrors.violation_type}
                          </ErrorMessage>
                        )}
                    </Field>

                    <Field>
                      <Label>Severity*</Label>
                      <FieldPicker
                        label="Severity"
                        options={severityOptions.map((severity) => ({
                          value: severity,
                          label: severity,
                        }))}
                        value={editedViolation?.severity}
                        onChange={(value) =>
                          handleInputChange({
                            target: {
                              name: 'severity',
                              value,
                              type: 'change',
                              checked: false,
                              nodeName: 'INPUT',
                            } as unknown as EventTarget & HTMLInputElement,
                          } as React.ChangeEvent<HTMLInputElement>)
                        }
                        placeholder="Select Severity"
                        invalid={!!(touchedFields.severity && formErrors.severity)}
                      />
                      {touchedFields.severity && formErrors.severity && (
                        <ErrorMessage>{formErrors.severity}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label>Status*</Label>
                      <FieldPicker
                        label="Status"
                        options={statusOptions}
                        value={editedViolation?.status}
                        onChange={(value) =>
                          handleInputChange({
                            target: {
                              name: 'status',
                              value,
                              type: 'change',
                              checked: false,
                              nodeName: 'INPUT',
                            } as unknown as EventTarget & HTMLInputElement,
                          } as React.ChangeEvent<HTMLInputElement>)
                        }
                        placeholder="Select Status"
                        invalid={!!(touchedFields.status && formErrors.status)}
                      />
                      {touchedFields.status && formErrors.status && (
                        <ErrorMessage>{formErrors.status}</ErrorMessage>
                      )}
                    </Field>
                  </div>

                  {/* Fine Amount and Due Date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="fine_amount">Fine Amount</Label>
                      <Input
                        id="fine_amount"
                        name="fine_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={editedViolation?.fine_amount || ''}
                        onChange={handleInputChange}
                        placeholder="Enter fine amount (optional)"
                        className="w-full"
                      />
                      {touchedFields.fine_amount && formErrors.fine_amount && (
                        <ErrorMessage>{formErrors.fine_amount}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label htmlFor="due_date">Due Date</Label>
                      <Input
                        id="due_date"
                        name="due_date"
                        type="date"
                        value={timestampToDateInput(editedViolation?.due_date)}
                        onChange={handleInputChange}
                        className="w-full"
                      />
                    </Field>
                  </div>

                  {/* Follow-up Date and Fine Paid Date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label>Follow-up Date</Label>
                      <Input
                        type="date"
                        name="follow_up_date"
                        value={
                          timestampToDateInput(editedViolation?.follow_up_date)
                        }
                        onChange={handleInputChange}
                        className="w-full"
                      />
                    </Field>

                  </div>

                  {/* Description and Message */}
                  <div className="space-y-4">
                    <Field>
                      <Label htmlFor="description">Description*</Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={editedViolation?.description ?? ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="300 character limit"
                        required
                        className="w-full"
                        data-invalid={
                          touchedFields.description && formErrors.description
                        }
                        rows={2}
                        maxLength={300}
                      />
                      {touchedFields.description && formErrors.description && (
                        <ErrorMessage>{formErrors.description}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label htmlFor="message">Detailed Message*</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={editedViolation?.message ?? ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Detailed description of the violation"
                        required
                        className="w-full"
                        data-invalid={
                          touchedFields.message && formErrors.message
                        }
                        rows={4}
                      />
                      {touchedFields.message && formErrors.message && (
                        <ErrorMessage>{formErrors.message}</ErrorMessage>
                      )}
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            </DialogBody>
          </div>

          <div className="sticky bottom-0 mt-auto border-t border-gray-200 dark:border-gray-700 bg-stone-100 dark:bg-gray-800 p-4 md:p-6">
            <div className="flex flex-col space-y-3">
              <Button
                onClick={handleSave}
                color="sky"
                disabled={isSaving || !isFormValid()}
                className="w-full disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={handleCancel}
                color="zinc"
                disabled={isSaving}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </SidebarLayout>
  );
};

export default ViolationDetailPage;
